import * as Sentry from '@sentry/gatsby'
import type { AxiosError } from 'axios'
import type { Dispatch, SetStateAction } from 'react'
import type { Product } from 'src/components/restructure/product/types/product'
import { handleAxiosError } from 'src/services/handleAxiosError'
import { getCollectionShelfData } from 'src/utils/recommendations/getShelfServerSideData'

type CollectionTab = {
  key: string
  idColecao?: string
}

type FetchCollectionProductsByTabParams = {
  tab: CollectionTab
  collectionProducts: Record<string, Product[]>
  setCollectionProducts: Dispatch<SetStateAction<Record<string, Product[]>>>
  setCollectionLoading: Dispatch<SetStateAction<Record<string, boolean>>>
  theme: 'light' | 'dark'
  sectionName: string
}

export async function fetchCollectionProductsByTab({
  tab,
  collectionProducts,
  setCollectionProducts,
  setCollectionLoading,
  theme,
  sectionName,
}: FetchCollectionProductsByTabParams): Promise<Product[]> {
  if (!tab.idColecao) {
    return []
  }

  const alreadyFetched = collectionProducts[tab.key]

  if (alreadyFetched) {
    return alreadyFetched
  }

  setCollectionLoading((prev) => ({ ...prev, [tab.key]: true }))

  try {
    const products = await getCollectionShelfData(
      [
        {
          text: tab.idColecao,
          id: tab.idColecao,
          engine: 'vtex',
        },
      ],
      {
        onError: (error) => {
          handleAxiosError(error as AxiosError)
          Sentry.captureException(error, {
            tags: {
              component: 'VitrineLarge',
              source: 'vtex',
              theme,
            },
            extra: {
              section: sectionName,
              tabKey: tab.key,
              tabIdCollection: tab.idColecao,
            },
          })
        },
      }
    )

    const normalizedProducts = products ?? []

    setCollectionProducts((prev) => ({
      ...prev,
      [tab.key]: normalizedProducts,
    }))

    return normalizedProducts
  } finally {
    setCollectionLoading((prev) => ({ ...prev, [tab.key]: false }))
  }
}
